import { Injectable } from '@angular/core';

import { DocumentsDataInner, ParagraphTestimonialData, ParagraphTestimonialSliderData } from '@generated/content';
import { Card, CmsComponentConfig, TestimonialSlider } from '@interfaces/config';
import { TMediaNodeType } from '@interfaces/media';
import { CmsComponentNodeData } from '@interfaces/cms-component-node-data';

import { BaseComponentResolver } from '@services/_base/base-component-resolver/base-component-resolver';
import { CardComponentResolver } from '@services/resolvers/component-resolvers/card-component/card-component-resolver';
import { MediaResolver } from '@services/resolvers/auxiliary/media-resolver';
import { CmsComponentTypeEnum } from '@models/enums';

@Injectable({ providedIn: 'root' })
export class TestimonialSliderComponentResolver extends BaseComponentResolver {
  static override instance: TestimonialSliderComponentResolver;
  private readonly componentType = TestimonialSlider.TypeEnum;

  static getInstance(): TestimonialSliderComponentResolver {
    return (TestimonialSliderComponentResolver.instance ||= new TestimonialSliderComponentResolver());
  }

  public override resolveComponentConfig(
    node: ParagraphTestimonialSliderData,
    nodePageData: CmsComponentNodeData[]
  ): CmsComponentConfig<TestimonialSlider.ComponentConfig> | null {
    this.nodePageData = nodePageData;

    const slides = this.buildSlides(node);

    return {
      componentType: this.componentType,
      config: { nodeId: BaseComponentResolver.getNodeId(node), slides },
    };
  }

  buildSlides(node: ParagraphTestimonialSliderData): TestimonialSlider.SlideConfig[] | null {
    if (!node.relationships?.field_testimonial_slider.data) {
      return null;
    }

    let nodes: ParagraphTestimonialData[];

    try {
      nodes = node.relationships.field_testimonial_slider.data.map(node => this.getNode(node.id, node.type));
    } catch (e) {
      return null;
    }

    return nodes.map(node => ({
      nodeId: BaseComponentResolver.getNodeId(node),
      headline: this.hyphenate(node.attributes.field_quote_testimonial),
      subHeadline: this.hyphenate(node.attributes.field_customer_testimonial),
      image: this.getImage(node),
      solutions: this.getSolutions(node),
    }));
  }

  getImage(node: ParagraphTestimonialData): string | null {
    if (!node.relationships?.field_media_testimonial.data) {
      return null;
    }

    const { id, type } = node.relationships.field_media_testimonial.data;
    const { dataUrls } = this.getMediaDataUrls(id, type as TMediaNodeType) || {};

    return dataUrls['webp'] || dataUrls['medium'] || dataUrls['media_library'] || null;
  }

  getSolutions(node: ParagraphTestimonialData): Card.ComponentConfig[] | null {
    let innerDocuments: DocumentsDataInner[];

    try {
      innerDocuments = node.relationships.field_document_testimonial.data;
    } catch (e) {
      return null;
    }

    const configs = CardComponentResolver.resolveComponentConfigs(innerDocuments, this.nodePageData) ?? [];

    return configs.map(config => {
      config.imageConfig.sizes = MediaResolver.buildImageConfigSizes(CmsComponentTypeEnum.TESTIMONIAL_SLIDER);
      return config;
    });
  }
}
